<div class="page-header">
    <div>
        <h4 class="section-title">Website widget channels</h4>
        <p class="page-subtitle">Each channel has its own public key. The widget works only from the allowed origins.</p>
    </div>
    <div class="flex-spacer"></div>
    <button *ngIf="perms.has(P.agentUpdate)" class="btn-primary btn-inline" (click)="openCreate()">
        <i class="fa-solid fa-plus"></i> Add web widget
    </button>
</div>

<div *ngIf="justSaved" class="card saved-card">
    <div class="page-header">
        <strong><i class="fa-solid fa-check"></i> Channel saved. Put this snippet on the website:</strong>
        <div class="flex-spacer"></div>
        <button class="icon-btn back" title="Close" (click)="justSaved = null"><i class="fa-solid fa-xmark"></i></button>
    </div>
    <div class="copy-row">
        <span class="mono">Public key: {{ justSaved.publicKey }}</span>
        <button class="btn-light btn-sm" (click)="copy(justSaved.publicKey, 'Public key copied')"><i class="fa-solid fa-copy"></i> Copy key</button>
    </div>
    <div class="code-box">{{ justSaved.embedSnippet }}</div>
    <div><button class="btn-secondary btn-sm btn-inline" (click)="copy(justSaved.embedSnippet, 'Embed snippet copied')"><i class="fa-solid fa-copy"></i> Copy snippet</button></div>
</div>

<div class="table-wrapper table-mobile-card">
    <table class="table" id="ai-channels-table">
        <thead>
            <tr>
                <th>Type</th>
                <th>Allowed origins</th>
                <th>Mode</th>
                <th>Public key</th>
                <th class="status-col">Enabled</th>
                <th class="actions-wide">Actions</th>
            </tr>
        </thead>
        <tbody>
            <tr *ngIf="!loading && channels.length === 0">
                <td colspan="100%">
                    <div class="no-data"><i class="fa-solid fa-code"></i> No channels yet</div>
                </td>
            </tr>
            <ng-container *ngFor="let c of channels">
                <tr>
                    <td data-label="Type"><span class="badge">{{ c.type }}</span></td>
                    <td data-label="Allowed origins">
                        <div *ngFor="let o of c.allowedOrigins" class="mono">{{ o }}</div>
                    </td>
                    <td data-label="Mode">{{ c.mode }}</td>
                    <td data-label="Public key">
                        <div class="copy-row">
                            <span class="mono">{{ c.publicKey }}</span>
                            <button class="icon-btn view" title="Copy public key" (click)="copy(c.publicKey, 'Public key copied')"><i class="fa-solid fa-copy"></i></button>
                        </div>
                    </td>
                    <td data-label="Enabled" class="status-col">
                        <span [ngClass]="c.enabled ? 'status-active' : 'status-disabled'">{{ c.enabled ? 'Yes' : 'No' }}</span>
                    </td>
                    <td data-label="Actions" class="actions-wide">
                        <div class="actions-cell">
                            <button class="icon-btn create" title="Copy embed snippet" (click)="copy(c.embedSnippet, 'Embed snippet copied')">
                                <i class="fa-solid fa-code"></i>
                            </button>
                            <button *ngIf="perms.has(P.agentUpdate)" class="icon-btn edit" title="Edit" (click)="openEdit(c)"><i class="fa-solid fa-pen"></i></button>
                            <button *ngIf="perms.has(P.agentUpdate)" class="icon-btn view" [title]="c.enabled ? 'Disable' : 'Enable'" (click)="toggleEnabled(c)">
                                <i class="fa-solid" [ngClass]="c.enabled ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
                            </button>
                            <button *ngIf="perms.has(P.agentUpdate)" class="icon-btn delete" title="Delete" (click)="remove(c)"><i class="fa-solid fa-trash"></i></button>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td colspan="100%">
                        <div class="code-box">{{ c.embedSnippet }}</div>
                    </td>
                </tr>
            </ng-container>
        </tbody>
    </table>
</div>

<div *ngIf="editorOpen" class="dialog-overlay">
    <div class="dialog-card" role="dialog" aria-modal="true">
        <div class="dialog-header">
            <div>
                <h3>{{ editingId ? 'Edit web widget' : 'Add web widget' }}</h3>
                <p>Allowed origins: the exact site addresses, e.g. the subdomain and the custom domain. Press Enter after each.</p>
            </div>
            <button type="button" class="icon-btn back" (click)="closeEditor()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <form class="dialog-body" (ngSubmit)="save()">
            <div class="form-field">
                <label for="ch-origins">Allowed origins</label>
                <p-chips inputId="ch-origins" name="allowedOrigins" [ngModel]="form.allowedOrigins" (ngModelChange)="originsChanged($event)"
                    [addOnBlur]="true" separator="," [allowDuplicate]="false" placeholder="https://www.example.com" styleClass="w-full"></p-chips>
                <span class="hint">"*" allows any site; use it only on purpose.</span>
                <span class="error-text" *ngIf="invalidOrigins.length">Not valid: {{ invalidOrigins.join(', ') }}</span>
            </div>
            <div class="two-col">
                <div class="form-field">
                    <label for="ch-mode">Mode</label>
                    <select id="ch-mode" name="mode" [(ngModel)]="form.mode">
                        <option *ngFor="let m of modes" [ngValue]="m">{{ m }}</option>
                    </select>
                </div>
                <div class="form-field">
                    <label for="ch-position">Position</label>
                    <select id="ch-position" name="position" [(ngModel)]="form.position">
                        <option *ngFor="let p of positions" [value]="p">{{ p }}</option>
                    </select>
                </div>
            </div>
            <div class="two-col">
                <div class="form-field">
                    <label for="ch-title">Widget title</label>
                    <input id="ch-title" name="title" [(ngModel)]="form.title" dir="auto" />
                </div>
                <div class="form-field">
                    <label for="ch-launcher">Launcher label</label>
                    <input id="ch-launcher" name="launcherLabel" [(ngModel)]="form.launcherLabel" dir="auto" placeholder="Chat with us" />
                </div>
            </div>
            <div class="form-field">
                <label for="ch-color">Primary color</label>
                <div class="copy-row">
                    <input id="ch-color" name="primaryColor" type="color" [(ngModel)]="form.primaryColor" style="width: 60px; padding: 2px" />
                    <input name="primaryColorText" [(ngModel)]="form.primaryColor" maxlength="20" />
                </div>
            </div>
            <label class="check-row"><input type="checkbox" name="enabled" [(ngModel)]="form.enabled" /> Enabled</label>
            <div *ngIf="formError" class="error-text">{{ formError }}</div>
            <div class="dialog-footer">
                <button type="button" class="btn-light" (click)="closeEditor()">Cancel</button>
                <button type="submit" class="btn-primary">Save</button>
            </div>
        </form>
    </div>
</div>
